import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../../api.js';
import { t } from '../../i18n/index.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { copyText } from '../../components/CopyField.js';
import { printInviteCards } from '../../components/InviteCards.js';
import { fileCount, saveInviteCards, type SaveFormat } from '../../components/InviteCardsExport.js';
import type { InviteBatchView, InviteCodeView, InviteKind } from '../../../shared/protocol.js';
import { INVITE_BATCH_LABEL_MAX, INVITE_BATCH_MAX_CODES } from '../../../shared/registration.js';

type Flash = { ok: boolean; text: string } | null;

const errorText = (err: unknown) => t.errors[err instanceof ApiError ? err.code : 'network'];
const formatDate = (ms: number) => new Date(ms).toLocaleDateString('fi-FI');
const formatTime = (ms: number) => new Date(ms).toLocaleString('fi-FI', { dateStyle: 'short', timeStyle: 'short' });

/**
 * Invite codes (decision 2026-09-28): registration on/off, new batches (label + count, valid 3
 * months), and per batch: codes, copy, print, pause/resume and revoke.
 */
export function InvitesAdmin() {
  const [open, setOpen] = useState<boolean | null>(null);
  const [batches, setBatches] = useState<InviteBatchView[]>([]);
  const [flash, setFlash] = useState<Flash>(null);

  const load = () =>
    api<{ open: boolean; batches: InviteBatchView[] }>('GET', '/api/admin/invites')
      .then((r) => {
        setOpen(r.open);
        setBatches(r.batches);
      })
      .catch((err) => setFlash({ ok: false, text: errorText(err) }));

  useEffect(() => {
    void load();
  }, []);

  const setRegistration = async (value: boolean) => {
    try {
      const r = await api<{ open: boolean }>('PUT', '/api/admin/invites/registration', { open: value });
      setOpen(r.open);
    } catch (err) {
      setFlash({ ok: false, text: errorText(err) });
    }
  };

  const i = t.admin.invites;
  return (
    <div class="stack">
      <section class="card">
        <h2>{i.registrationTitle}</h2>
        {open !== null && (
          <div class="row-between">
            <p class={open ? 'success' : 'error'}>{open ? i.registrationOpen : i.registrationClosed}</p>
            <button class={`btn btn-small ${open ? '' : 'btn-primary'}`} onClick={() => void setRegistration(!open)}>
              {open ? i.deny : i.allow}
            </button>
          </div>
        )}
      </section>
      <CreateBatchForm
        suggestion={`Erä ${batches.length + 1}`}
        onCreated={(label, count) => {
          setFlash({ ok: true, text: i.created(label, count) });
          void load();
        }}
      />
      <section class="card">
        <h2>{i.batchesTitle}</h2>
        {flash && (
          <p class={flash.ok ? 'success' : 'error'} role="status">
            {flash.text}
          </p>
        )}
        {batches.length === 0 ? (
          <p class="muted">{i.none}</p>
        ) : (
          <ul class="user-list">
            {batches.map((b) => (
              <BatchRow key={b.id} batch={b} onChanged={() => void load()} onFlash={setFlash} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function CreateBatchForm(props: { suggestion: string; onCreated: (label: string, count: number) => void }) {
  const [label, setLabel] = useState('');
  const [count, setCount] = useState(10);
  const [kind, setKind] = useState<InviteKind>('register');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const name = label.trim() || props.suggestion;
    try {
      await api('POST', '/api/admin/invites/batches', { label: name, count, kind });
      props.onCreated(name, count);
      setLabel('');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const i = t.admin.invites;
  return (
    <form class="card form" onSubmit={submit}>
      <h2>{i.newTitle}</h2>
      <fieldset class="card-format">
        <legend>{i.kind}</legend>
        <label class="radio">
          <input type="radio" name="batch-kind" checked={kind === 'register'} onChange={() => setKind('register')} />
          {i.kindRegister}
        </label>
        <label class="radio">
          <input type="radio" name="batch-kind" checked={kind === 'guest'} onChange={() => setKind('guest')} />
          {i.kindGuest}
        </label>
      </fieldset>
      <p class="hint">{kind === 'guest' ? i.newInfoGuest : i.newInfo}</p>
      <div class="grid-2">
        <label>
          {i.label}
          <input value={label} placeholder={props.suggestion} maxLength={INVITE_BATCH_LABEL_MAX} onInput={(e) => setLabel(e.currentTarget.value)} />
        </label>
        <label>
          {i.count}
          <input
            type="number"
            min={1}
            max={INVITE_BATCH_MAX_CODES}
            value={count}
            onInput={(e) => setCount(Number(e.currentTarget.value))}
            required
          />
        </label>
      </div>
      {error && (
        <p class="error" role="alert">
          {error}
        </p>
      )}
      <button class="btn btn-primary" type="submit" disabled={busy}>
        {i.create}
      </button>
    </form>
  );
}

function BatchRow(props: { batch: InviteBatchView; onChanged: () => void; onFlash: (f: Flash) => void }) {
  const { batch } = props;
  const [codes, setCodes] = useState<InviteCodeView[] | null>(null);
  const [showCodes, setShowCodes] = useState(false);
  const [cardsDialog, setCardsDialog] = useState<'print' | 'save' | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const expired = batch.expiresAt < Date.now();
  const state = batch.status === 'active' && expired ? 'expired' : batch.status;
  const usable = state === 'active' || state === 'paused';

  const loadCodes = async (): Promise<InviteCodeView[] | null> => {
    try {
      const r = await api<{ codes: InviteCodeView[] }>('GET', `/api/admin/invites/batches/${batch.id}/codes`);
      setCodes(r.codes);
      return r.codes;
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
      return null;
    }
  };

  const toggleCodes = async () => {
    if (!showCodes) await loadCodes();
    setShowCodes(!showCodes);
  };

  const copyUnused = async () => {
    const list = (await loadCodes())?.filter((c) => c.usedAt === null) ?? [];
    if (list.length === 0) return props.onFlash({ ok: false, text: t.admin.invites.noUnused });
    const ok = await copyText(list.map((c) => c.code).join('\n'));
    props.onFlash(ok ? { ok: true, text: t.admin.invites.copiedN(list.length) } : { ok: false, text: t.admin.copyFailed });
  };

  const openCards = async (mode: 'print' | 'save') => {
    if (await loadCodes()) setCardsDialog(mode);
  };

  const setStatus = async (status: InviteBatchView['status']) => {
    try {
      await api('PATCH', `/api/admin/invites/batches/${batch.id}`, { status });
      props.onFlash(null);
      props.onChanged();
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    }
  };

  /** Revoking deletes the batch and its codes (decision 2026-09-29). */
  const revoke = async () => {
    setConfirmRevoke(false);
    try {
      await api('DELETE', `/api/admin/invites/batches/${batch.id}`);
      props.onFlash({ ok: true, text: t.admin.invites.revoked(batch.label) });
      props.onChanged();
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    }
  };

  const i = t.admin.invites;
  return (
    <li class={`user-row batch-row ${usable ? '' : 'is-locked'}`}>
      <div class="user-main">
        <div class="user-name">
          {batch.label} <span class={`badge batch-${state}`}>{i.status[state]}</span>
          {batch.kind === 'guest' && <span class="badge batch-guest">{i.guestBadge}</span>}
        </div>
        <div class="user-sub">
          {i.used(batch.used, batch.total)} · {expired ? i.expiredOn(formatDate(batch.expiresAt)) : i.validUntil(formatDate(batch.expiresAt))}
          {batch.createdBy && ` · ${i.createdBy(batch.createdBy)} ${formatDate(batch.createdAt)}`}
        </div>
        {batch.status === 'paused' && <div class="user-sub">{i.pauseInfo}</div>}
      </div>
      <div class="user-actions">
        <button class="btn btn-small" onClick={() => void toggleCodes()} aria-expanded={showCodes}>
          {showCodes ? i.hideCodes : i.showCodes}
        </button>
        {usable && (
          <>
            <button class="btn btn-small" onClick={() => void copyUnused()}>
              {i.copyUnused}
            </button>
            <button class="btn btn-small" onClick={() => void openCards('print')}>
              🖨 {i.print}
            </button>
            <button class="btn btn-small" onClick={() => void openCards('save')}>
              💾 {i.save}
            </button>
            <button class="btn btn-small" onClick={() => void setStatus(batch.status === 'active' ? 'paused' : 'active')}>
              {batch.status === 'active' ? i.pause : i.resume}
            </button>
          </>
        )}
        <button class="btn btn-small btn-danger" onClick={() => setConfirmRevoke(true)}>
          {usable ? i.revoke : i.remove}
        </button>
      </div>
      {showCodes && codes && (
        <ul class="code-list">
          {codes.map((c) => (
            <li key={c.code} class={c.usedAt ? 'used' : ''}>
              <code>{c.code}</code>
              <span>
                {c.usedAt === null
                  ? i.unused
                  : c.usedBy
                    ? i.usedBy(c.usedBy, formatTime(c.usedAt))
                    : batch.kind === 'guest'
                      ? i.guestVisited(formatTime(c.usedAt))
                      : i.usedByDeleted(formatTime(c.usedAt))}
              </span>
            </li>
          ))}
        </ul>
      )}
      {cardsDialog && codes && <CardsDialog mode={cardsDialog} batch={batch} codes={codes} onClose={() => setCardsDialog(null)} />}
      {confirmRevoke && (
        <ConfirmDialog
          title={usable ? i.revokeTitle(batch.label) : i.removeTitle(batch.label)}
          confirmLabel={usable ? i.revoke : i.remove}
          cancelLabel={t.admin.cancel}
          danger
          onConfirm={() => void revoke()}
          onCancel={() => setConfirmRevoke(false)}
        >
          <p>{i.revokeText}</p>
        </ConfirmDialog>
      )}
    </li>
  );
}

/**
 * Print or save the cards: QR code on the cards, only unused codes; when saving also the format
 * (PDF sheets for printing, JPG sheets or one JPG per card for sharing).
 */
function CardsDialog(props: { mode: 'print' | 'save'; batch: InviteBatchView; codes: InviteCodeView[]; onClose: () => void }) {
  const [withQr, setWithQr] = useState(true);
  const [onlyUnused, setOnlyUnused] = useState(true);
  const [format, setFormat] = useState<SaveFormat>('pdf');
  const [status, setStatus] = useState<Flash>(null);
  const [busy, setBusy] = useState(false);
  const cards = props.codes
    .filter((c) => !onlyUnused || c.usedAt === null)
    .map((c) => ({ code: c.code, expiresAt: props.batch.expiresAt, kind: props.batch.kind }));
  const i = t.admin.invites;
  const saving = props.mode === 'save';

  const save = async () => {
    if (busy || cards.length === 0) return;
    setBusy(true);
    setStatus({ ok: true, text: i.saving });
    try {
      await saveInviteCards(cards, withQr, format, props.batch.label);
      setStatus({ ok: true, text: i.saved(fileCount(format, cards.length)) });
    } catch {
      setStatus({ ok: false, text: i.saveFailed });
    } finally {
      setBusy(false);
    }
  };

  const formatOption = (value: SaveFormat, label: string) => (
    <label class="radio">
      <input type="radio" name="card-format" checked={format === value} onChange={() => setFormat(value)} />
      {label}
    </label>
  );

  return (
    <ConfirmDialog
      title={saving ? i.saveTitle(props.batch.label) : i.printTitle(props.batch.label)}
      confirmLabel={saving ? `💾 ${i.saveNow}` : `🖨 ${i.printNow}`}
      cancelLabel={t.admin.close}
      onConfirm={() => (saving ? void save() : cards.length > 0 && printInviteCards(cards, withQr))}
      onCancel={props.onClose}
    >
      <div class="form">
        {saving && (
          <fieldset class="card-format">
            <legend>{i.format}</legend>
            {formatOption('pdf', i.formatPdf)}
            {formatOption('jpg-sheets', i.formatJpgSheets)}
            {formatOption('jpg-cards', i.formatJpgCards)}
          </fieldset>
        )}
        <label class="switch">
          <input type="checkbox" checked={withQr} onChange={(e) => setWithQr(e.currentTarget.checked)} />
          <span class="switch-track" aria-hidden="true" />
          {i.printQr}
        </label>
        <label class="switch">
          <input type="checkbox" checked={onlyUnused} onChange={(e) => setOnlyUnused(e.currentTarget.checked)} />
          <span class="switch-track" aria-hidden="true" />
          {i.printOnlyUnused}
        </label>
        <p>
          {cards.length === 0
            ? i.noUnused
            : saving && format !== 'pdf'
              ? i.saveCount(cards.length, fileCount(format, cards.length))
              : i.printCount(cards.length)}
        </p>
        {status && (
          <p class={status.ok ? 'success' : 'error'} role="status">
            {status.text}
          </p>
        )}
        <p class="hint">{saving ? i.saveHint : i.printHint}</p>
      </div>
    </ConfirmDialog>
  );
}
